<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { useI18n } from "vue-i18n"

// ================================
// TYPES & INTERFACES
// ================================
//
// DEV-255 (mirror DEV-83 — pattern "capteur inexistant") — banner affiché dans
// SensorInfoDisplay quand `informations.iot.etat === "modele_incompatible"`.
//
// L'état "modèle incompatible" arrive quand les payloads reçus de l'opérateur
// ne peuvent pas être décodés par le modèle de capteur configuré (cf. DEV-247
// côté data : le worker `api_workers.get_data()` lève `BadModelError` →
// `monitoring.statuscodelastretrieval=422` → dapi DEV-254 → `etat`).
//
// Le banner ne contient AUCUNE logique de re-sélection : il émet juste
// `edit-sensor` que le parent capte pour ouvrir le SourceAppendDialog en mode
// édition (workflow déjà éprouvé pour DEV-83). L'utilisateur arrive sur la
// page de re-sélection du modèle (et peut aussi changer la marque ou
// l'opérateur si besoin).
//
type Props = {
    /**
     * Nom de l'opérateur du capteur. Affiché dans le wording explicatif.
     */
    operator: string
    /**
     * Identifiant du modèle actuellement configuré pour le capteur (incompatible).
     * Affiché dans le wording pour aider l'utilisateur à identifier ce qu'il faut
     * changer.
     */
    model?: string
}

// ================================
// PROPS / EMITS
// ================================
withDefaults(defineProps<Props>(), {
    model: "",
})

const emit = defineEmits<{
    /**
     * Demande d'ouverture du SourceAppendDialog en mode édition (re-sélection
     * du modèle). Le parent doit câbler ça pour ouvrir le dialog avec le
     * sourceId du capteur.
     */
    (e: "edit-sensor"): void
}>()

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            tag: {
                model_incompatible: "Modèle incompatible",
            },
            tooltip: {
                model_incompatible:
                    "Le modèle configuré ne permet pas de décoder les données reçues de l'opérateur",
            },
            warning: {
                message_with_model:
                    "Le modèle « {model} » ne permet pas de décoder les données envoyées par {operator}. Le capteur reçoit bien des messages mais ils ne peuvent pas être interprétés. Sélectionnez un autre modèle pour reprendre la collecte.",
                message_without_model:
                    "Le modèle configuré ne permet pas de décoder les données envoyées par {operator}. Le capteur reçoit bien des messages mais ils ne peuvent pas être interprétés. Sélectionnez un autre modèle pour reprendre la collecte.",
                button: "Changer le modèle",
            },
        },
        en: {
            tag: {
                model_incompatible: "Incompatible model",
            },
            tooltip: {
                model_incompatible:
                    "The configured model cannot decode the data received from the operator",
            },
            warning: {
                message_with_model:
                    'The model "{model}" cannot decode the data sent by {operator}. The sensor is receiving messages but they cannot be interpreted. Select a different model to resume data collection.',
                message_without_model:
                    "The configured model cannot decode the data sent by {operator}. The sensor is receiving messages but they cannot be interpreted. Select a different model to resume data collection.",
                button: "Change model",
            },
        },
    },
})

// ================================
// EVENT HANDLERS
// ================================

/**
 * Demande au parent d'ouvrir SourceAppendDialog en mode édition.
 */
function onEditSensor() {
    emit("edit-sensor")
}
</script>

<template>
    <!--
        DEV-255 (mirror DEV-83) — banner "modèle incompatible".

        Le badge "Modèle incompatible" déménage dans la section Modèle de
        SensorInfoDisplay (à côté de la valeur du modèle). Le banner ici se
        concentre sur le message + l'action de changement.

        Pas de modal interne : l'action "Changer le modèle" délègue au parent
        qui ouvre le SourceAppendDialog en mode édition (workflow déjà éprouvé
        via sensor-form.machine.ts).

        Severity "warn" (orange) au lieu de "error" (rouge) car c'est une
        erreur de configuration côté user, pas un défaut chez l'opérateur — la
        donnée arrive correctement, c'est juste qu'on ne sait pas la lire.
    -->
    <div>
        <BaseMessage
            severity="warn"
            :closable="false"
            id="sensor-model-incompatible-message"
            role="status"
            aria-live="polite"
        >
            <div class="flex flex-col gap-3">
                <p class="m-0">
                    <template v-if="model">
                        {{
                            t("warning.message_with_model", { model, operator })
                        }}
                    </template>
                    <template v-else>
                        {{ t("warning.message_without_model", { operator }) }}
                    </template>
                </p>
                <div class="flex items-center gap-2">
                    <BaseButton
                        :label="t('warning.button')"
                        icon="pi pi-cog"
                        size="small"
                        severity="warn"
                        outlined
                        aria-describedby="sensor-model-incompatible-message"
                        @click="onEditSensor"
                    />
                </div>
            </div>
        </BaseMessage>
    </div>
</template>
